import { useEffect, useMemo, useState } from 'react';
import { Spin, Tabs, TabsProps } from 'antd';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import PatientInformationHeader from '../../components/Patient/PatientInformationHeader';
import {
    useGetOrderTemplatesQuery,
    useGetPatientDetailsQuery,
    useGetPatientMedicationsQuery,
} from '../../features/patient/patientService';
import RoundedButton from '../../components/RoundedButton';
import ConfirmationModal from '../../components/Orders/ConfirmationModal';
import PageTitle from '../../components/PageTitle';
import RenderSideItem from '../../components/RenderSideItem';
import MedOrdersCard from '../../components/MedOrders/MedOrdersCard';
import moment from 'moment';
import { MedOrderResponse, OrderSourceEnum } from '../../models/PatientModels';
import MedSheetsTab from '../../components/Orders/MedSheets';
import { Utility } from '../../utilities/Utility';
import { MedicationHistoryTabKeys } from '../../global/consts';

const MedHistory = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { id: patientId = '' } = useParams();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
    const [params] = useSearchParams();

    const { data: medSheets, isLoading: medsheetsLoading, refetch: refetchMedsheets } = useGetOrderTemplatesQuery({
        patientId: patientId,
        source: OrderSourceEnum.EMR,
    });

    const { data: patientDetails, isLoading: patientDetailsLoading } =
        useGetPatientDetailsQuery({
            id: patientId,
        });

    const { data: patientMedications, isLoading, refetch: refetchPatientMedications } =
        useGetPatientMedicationsQuery({
            patientId: patientId,
        });

    const activeMedications = useMemo(() => {
        if (patientMedications && patientMedications?.length > 0) {
            const activeMeds = patientMedications.filter((pm) => {
                return moment(pm.endDate).isSameOrAfter(moment());
            });
            return activeMeds;
        }
        return [];
    }, [patientMedications]);

    const discontinuedMedications = useMemo(() => {
        if (patientMedications && patientMedications?.length > 0) {
            const activeMeds = patientMedications.filter((pm) => {
                return moment(pm.endDate).isBefore(moment());
            });
            return activeMeds;
        }
        return [];
    }, [patientMedications]);

    const isDisabled =
        isLoading || (patientMedications && patientMedications.length === 0);

    const items: TabsProps['items'] = useMemo(() => {
        return [
            {
                key: `${MedicationHistoryTabKeys.Medsheets}`,
                label: 'Med Sheets',
                children: <MedSheetsTab medSheets={medSheets} isLoading={medsheetsLoading} refetchMedsheets={refetchMedsheets} />,
            },
            {
                key: `${MedicationHistoryTabKeys.FulfilledMedications}`,
                label: 'Fulfilled Medications',
                children: (
                    <MedOrdersCard
                        data={activeMedications as MedOrderResponse[]}
                        isLoading={isLoading || patientDetailsLoading}
                        organizationImplementationDate={Utility.getFormattedDate(patientDetails?.hospiceOrganizationImplementationDate)}
                    />
                ),
            },
            // {
            //     key: `${MedicationHistoryTabKeys.DiscontinuedMedications}`,
            //     label: 'Discontinued Medications',
            //     children: (
            //         <MedOrdersCard
            //             data={discontinuedMedications as MedOrderResponse[]}
            //             isLoading={isLoading || patientDetailsLoading}
            //             organizationImplementationDate={Utility.getFormattedDate(patientDetails?.hospiceOrganizationImplementationDate)}
            //         />
            //     ),
            // },
        ];
    }, [activeMedications, discontinuedMedications, isLoading, patientDetailsLoading]);

    const [activeKey, setActiveKey] = useState(`${MedicationHistoryTabKeys.Medsheets}`);

    useEffect(() => {
        const currTabSelected = params.get("tab");

        if (!currTabSelected) {
            setActiveKey(`${MedicationHistoryTabKeys.Medsheets}`);
        }
        else {
            setActiveKey(currTabSelected);
        }
    }, [])

    const onTabSelected = (value: string) => {
        setActiveKey(value);
        navigate(`${location.pathname}?tab=${value}`);

        if (value != `${MedicationHistoryTabKeys.Medsheets}`) {
            refetchPatientMedications();
        }
        else {
            refetchMedsheets();
        }
    }

    return (
        <div className="">
            <PageTitle
                title="Medication History"
                onClick={() => navigate(`/patient/${patientId}`)}
            />
            <div className="mx-5">
                {patientDetails && (
                    <PatientInformationHeader
                        patient={patientDetails}
                        navigate={() =>navigate(`/patient/${patientId}`)}
                    >
                        <RenderSideItem
                            link={`/patient/${patientId}/allergies`}
                            title="Drug Allergies"
                            iconSource="/assets/rx-images/patient-dashboard/patient-allergies.png"
                        />
                    </PatientInformationHeader>
                )}
                <Tabs
                    className="mt-4 order-card-tabs"
                    activeKey={activeKey}
                    onChange={onTabSelected}
                    items={items}
                    tabBarExtraContent={{
                        right: (
                            <div className="d-flex gap-3">
                                <RoundedButton
                                    onClick={() =>
                                        navigate(
                                            `/patient/${patientId}/prescriptions`,
                                        )
                                    }
                                    style={{
                                        ...(!isDisabled
                                            ? {
                                                    backgroundColor:
                                                        'var(--hcrx-medium-blue)',
                                                    color: 'white',
                                                }
                                            : {}),
                                        fontWeight: 500,
                                    }}
                                    disabled={isDisabled}
                                >
                                    <div className="d-flex gap-2">
                                        Access Re-prescribe
                                    </div>
                                </RoundedButton>
                            </div>
                        ),
                    }}
                />
            </div>
        </div>
    );
};

export default MedHistory;
